// Extensions API — first-party connectors to ecosystem services.
import { apiClient } from './client'

export type HealthStatus = 'healthy' | 'degraded' | 'unreachable'

export interface Extension {
  id: string
  name: string
  description: string
  enabled: boolean
  /** Effective URL — DB override if set, registry default otherwise. Null for built-in extensions (no endpoint). */
  url: string | null
  default_url: string | null
  advertise_when_disabled: boolean
  /** Deployment feature flag. False ⇒ force-disabled — the admin toggle is inert
   * and the backend rejects enabling with a 409. */
  available: boolean
  /** Extension ids that must be enabled for this one to be (ADR-0051). */
  requires: string[]
  /** Required extensions that are off now: this one reads as off until they are on,
   * and enabling it is refused with a 409 `extension_requires`. */
  missing_requirements: string[]
}

export interface ExtensionUpdate {
  enabled?: boolean
  /** Null/empty reverts to the registry default. */
  url?: string | null
}

export interface ExtensionHealth {
  status: HealthStatus
  latency_ms: number
  error?: string | null
  /** What the probe learned beyond the status, e.g. the faces service's recognizer (HUMINT). */
  detail?: string | null
}

export interface GeocodeResult {
  id: string
  display: string
  street: string | null
  housenumber: string | null
  city: string | null
  postcode: string | null
  country: string | null
  lat: number
  lon: number
}

export interface GeocodeResponse {
  query: string
  found: number
  took_ms: number
  results: GeocodeResult[]
}

export const extensionsApi = {
  list: () => apiClient.get<Extension[]>('/v1/extensions'),

  get: (id: string) => apiClient.get<Extension>(`/v1/extensions/${id}`),

  patch: (id: string, data: ExtensionUpdate) =>
    apiClient.patch<Extension>(`/v1/extensions/${id}`, data),

  health: (id: string) => apiClient.get<ExtensionHealth>(`/v1/extensions/${id}/health`),

  geocode: (q: string, limit: number = 5) =>
    apiClient.post<GeocodeResponse>('/v1/extensions/geocoding/geocode', { q, limit }),
}
